<template>
  <div class="task-manager container">
    <el-card>
      <div class="filter-bar">
        <el-select v-model="filters.projectId" placeholder="项目筛选" clearable style="width: 150px; margin-right: 10px;" @change="loadTasks">
          <el-option label="全部项目" value="" />
          <el-option v-for="project in projectList" :key="project.project_id" :label="project.name" :value="project.project_id" />
        </el-select>
        <el-select v-model="filters.assigneeId" placeholder="执行人筛选" clearable style="width: 150px; margin-right: 10px;">
          <el-option label="全部执行人" value="" />
          <el-option v-for="robot in robotList" :key="robot.robot_id" :label="robot.robot_name" :value="robot.robot_id" />
        </el-select>
        <el-select v-model="filters.status" placeholder="状态筛选" clearable style="width: 120px; margin-right: 10px;">
          <el-option label="全部状态" value="" />
          <el-option label="待执行" value="1" />
          <el-option label="等依赖" value="2" />
          <el-option label="执行中" value="3" />
          <el-option label="已完成" value="4" />
          <el-option label="已失败" value="5" />
        </el-select>
        <el-select v-model="filters.priority" placeholder="优先级筛选" clearable style="width: 120px; margin-right: 10px;">
          <el-option label="全部优先级" value="" />
          <el-option label="高" value="1" />
          <el-option label="中" value="2" />
          <el-option label="低" value="3" />
        </el-select>
        <el-input v-model="keywords" placeholder="搜索任务名称" clearable style="width: 200px; margin-right: 10px;"></el-input>
        <el-button type="primary" @click="loadTasks">搜索</el-button>
        <el-button type="success" @click="toggleView">切换视图</el-button>
        <el-button type="primary" @click="showCreateDialog">新建任务</el-button>
      </div>

      <div v-loading="loading">
        <!-- 列表视图 -->
        <div v-if="viewType === 'list'">
          <el-table :data="taskList" style="width: 100%">
            <el-table-column prop="task_name" label="任务名称" min-width="200" show-overflow-tooltip />
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                <el-tag :type="getStatusTagType(scope.row.status)">{{ getStatusText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="priority" label="优先级" width="80">
              <template slot-scope="scope">
                <el-tag :type="getPriorityTagType(scope.row.priority)">{{ getPriorityText(scope.row.priority) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="receiver_id" label="执行人" width="120" show-overflow-tooltip />
            <el-table-column prop="finish_time" label="截止时间" width="120">
              <template slot-scope="scope">{{ formatTime(scope.row.finish_time) }}</template>
            </el-table-column>
            <el-table-column prop="project_id" label="所属项目" width="150" show-overflow-tooltip />
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="goToDetail(scope.row.task_id)">查看详情</el-button>
                <el-button type="text" size="small" @click="showEditDialog(scope.row)">编辑</el-button>
                <el-button type="text" size="small" class="danger" @click="deleteTask(scope.row.task_id)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.currentPage"
              :page-sizes="[10, 20, 50]"
              :total="pagination.total"
              layout="sizes, prev, pager, next, jumper, total"
            ></el-pagination>
          </div>
        </div>

        <!-- 看板视图 -->
        <div v-if="viewType === 'kanban'">
          <el-row :gutter="20">
            <el-col :span="6" v-for="status in statusList" :key="status.value">
              <div class="kanban-column">
                <div class="column-header">
                  <h3>{{ status.label }}</h3>
                  <span class="count">{{ getTasksByStatus(status.value).length }}</span>
                </div>
                <div class="kanban-cards">
                  <div v-for="task in getTasksByStatus(status.value)" :key="task.task_id" class="task-card">
                    <div class="card-content">
                      <h4>{{ task.task_name }}</h4>
                      <p class="task-desc">{{ task.task_description }}</p>
                      <div class="task-meta">
                        <span class="assignee">{{ task.receiver_id }}</span>
                        <span class="due-time">{{ formatTime(task.finish_time) }}</span>
                      </div>
                      <div class="task-actions">
                        <el-button type="text" size="small" @click="goToDetail(task.task_id)">查看详情</el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <div v-if="!taskList.length && !loading" class="empty-text">暂无数据</div>
      </div>
    </el-card>

    <!-- 新建任务对话框 -->
    <el-dialog title="新建任务" :visible.sync="createDialogVisible" width="600px">
      <el-form :model="newTask" :rules="createRules" ref="createForm">
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model="newTask.taskName" placeholder="请输入任务名称"></el-input>
        </el-form-item>
        <el-form-item label="任务描述" prop="taskDesc">
          <el-input type="textarea" v-model="newTask.taskDesc" placeholder="请输入任务描述" :rows="3"></el-input>
        </el-form-item>
        <el-form-item label="所属项目" prop="projectId">
          <el-select v-model="newTask.projectId" placeholder="请选择项目">
            <el-option v-for="project in projectList" :key="project.project_id" :label="project.name" :value="project.project_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="执行人" prop="assigneeId">
          <el-select v-model="newTask.assigneeId" placeholder="请选择执行人">
            <el-option v-for="robot in robotList" :key="robot.robot_id" :label="robot.robot_name" :value="robot.robot_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="优先级" prop="priority">
          <el-select v-model="newTask.priority" placeholder="请选择优先级">
            <el-option label="高" value="1" />
            <el-option label="中" value="2" />
            <el-option label="低" value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="截止时间" prop="dueTime">
          <el-date-picker
            v-model="newTask.dueTime"
            type="datetime"
            placeholder="选择截止时间"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="createTask">创建</el-button>
      </div>
    </el-dialog>

    <!-- 编辑任务对话框 -->
    <el-dialog title="编辑任务" :visible.sync="editDialogVisible" width="600px">
      <el-form :model="editTask" :rules="editRules" ref="editForm">
        <el-form-item label="任务名称" prop="taskName">
          <el-input v-model="editTask.taskName" placeholder="请输入任务名称"></el-input>
        </el-form-item>
        <el-form-item label="任务描述" prop="taskDesc">
          <el-input type="textarea" v-model="editTask.taskDesc" placeholder="请输入任务描述" :rows="3"></el-input>
        </el-form-item>
        <el-form-item label="执行人" prop="assigneeId">
          <el-select v-model="editTask.assigneeId" placeholder="请选择执行人">
            <el-option v-for="robot in robotList" :key="robot.robot_id" :label="robot.robot_name" :value="robot.robot_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="优先级" prop="priority">
          <el-select v-model="editTask.priority" placeholder="请选择优先级">
            <el-option label="高" value="1" />
            <el-option label="中" value="2" />
            <el-option label="低" value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="截止时间" prop="dueTime">
          <el-date-picker
            v-model="editTask.dueTime"
            type="datetime"
            placeholder="选择截止时间"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="editTask.status" placeholder="请选择状态">
            <el-option label="待执行" value="1" />
            <el-option label="等依赖" value="2" />
            <el-option label="执行中" value="3" />
            <el-option label="已完成" value="4" />
            <el-option label="已失败" value="5" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="updateTask">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getTaskListPaged, getMyAllTasks, createTask, updateTask, deleteTask } from '../api/task'
import { getMyProjectList } from '../api/project'
import { getRobotList } from '../api/robot'

export default {
  name: 'TaskManager',
  data() {
    return {
      loading: false,
      taskList: [],
      projectList: [],
      robotList: [],
      viewType: 'list',
      keywords: '',
      filters: {
        projectId: '',
        assigneeId: '',
        status: '',
        priority: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      statusList: [
        { value: '1', label: '待执行' },
        { value: '2', label: '等依赖' },
        { value: '3', label: '执行中' },
        { value: '4', label: '已完成' },
        { value: '5', label: '已失败' }
      ],
      createDialogVisible: false,
      editDialogVisible: false,
      newTask: {
        taskName: '',
        taskDesc: '',
        projectId: '',
        assigneeId: '',
        priority: '2',
        dueTime: ''
      },
      editTask: {
        taskId: '',
        taskName: '',
        taskDesc: '',
        projectId: '',
        assigneeId: '',
        priority: '2',
        dueTime: '',
        status: '1'
      },
      createRules: {
        taskName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        projectId: [{ required: true, message: '请选择所属项目', trigger: 'change' }],
        assigneeId: [{ required: true, message: '请选择执行人', trigger: 'change' }],
        dueTime: [{ required: true, message: '请选择截止时间', trigger: 'change' }]
      },
      editRules: {
        taskName: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        assigneeId: [{ required: true, message: '请选择执行人', trigger: 'change' }],
        dueTime: [{ required: true, message: '请选择截止时间', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    }
  },
  async created() {
    this.initFromQuery()
    // 先加载项目列表，再加载任务（loadTasks 会用到项目列表）
    await this.loadProjects()
    await this.loadRobots()
    await this.loadTasks()
  },
  watch: {
    '$route.query': {
      handler() {
        this.initFromQuery()
        this.loadTasks()
      },
      deep: true
    }
  },
  methods: {
    initFromQuery() {
      const query = this.$route.query
      if (query.filter === 'mine') {
        // 我的任务筛选 - 需要从用户信息获取当前用户ID
        const userId = this.$store.getters['user/userId']
        if (userId) {
          this.filters.assigneeId = userId
        }
      }
      if (query.project) {
        this.filters.projectId = query.project
      }
    },
    async loadTasks() {
      this.loading = true
      try {
        // 如果选择了特定项目，只加载该项目的任务
        if (this.filters.projectId) {
          const params = {
            project_id: this.filters.projectId,
            page: this.pagination.currentPage,
            page_size: this.pagination.pageSize,
            status: this.filters.status || undefined,
            assignee_id: this.filters.assigneeId || undefined,
            priority: this.filters.priority || undefined,
            keywords: this.keywords || undefined
          }
          const data = await getTaskListPaged(params)
          this.taskList = data?.list || []
          this.pagination.total = data?.total || 0
        } else {
          // 加载所有项目的任务（使用后台统一接口）
          await this.loadAllTasksFromBackend()
        }
      } catch (error) {
        console.error('获取任务列表失败:', error)
        this.$message.error(`获取任务列表失败: ${error.message || '未知错误'}`)
        this.taskList = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    async loadAllTasksFromBackend() {
      // 使用后台统一接口获取所有项目的任务
      const params = {
        page: this.pagination.currentPage,
        page_size: this.pagination.pageSize,
        status: this.filters.status || undefined,
        assignee_id: this.filters.assigneeId || undefined,
        priority: this.filters.priority || undefined,
        keywords: this.keywords || undefined
      }
      
      const data = await getMyAllTasks(params)
      this.taskList = data?.list || []
      this.pagination.total = data?.total || 0
    },
    async loadProjects() {
      try {
        const data = await getMyProjectList()
        this.projectList = data?.list || []
        if (this.projectList.length === 0) {
          // 有项目但加载失败时才提示
        }
      } catch (error) {
        console.error('获取项目列表失败:', error)
        // 不显示错误提示，因为这是正常的业务场景（没有项目）
        this.projectList = []
      }
    },
    async loadRobots() {
      try {
        const data = await getRobotList()
        this.robotList = data?.list || []
      } catch (error) {
        console.error('获取机器人列表失败:', error)
        this.$message.error(`获取机器人列表失败: ${error.message || '未知错误'}`)
      }
    },
    toggleView() {
      this.viewType = this.viewType === 'list' ? 'kanban' : 'list'
    },
    getTasksByStatus(status) {
      return this.taskList.filter(task => task.status === status)
    },
    showCreateDialog() {
      this.createDialogVisible = true
      this.newTask = {
        taskName: '',
        taskDesc: '',
        projectId: '',
        assigneeId: '',
        priority: '2',
        dueTime: ''
      }
    },
    async createTask() {
      try {
        // 转换字段格式为后端需要的格式 (snake_case)
        const data = {
          task_name: this.newTask.taskName,
          description: this.newTask.taskDesc,
          project_id: this.newTask.projectId,
          receiver_id: this.newTask.assigneeId,
          sender_id: this.$store.getters['user/userId'] || this.newTask.assigneeId,
          priority: parseInt(this.newTask.priority) || 2,
          finish_time: this.newTask.dueTime ? new Date(this.newTask.dueTime).toISOString() : null,
          task_type: 1
        }
        await createTask(data)
        this.createDialogVisible = false
        this.$message.success('任务创建成功')
        this.loadTasks()
      } catch (error) {
        console.error('创建任务失败:', error)
        this.$message.error(`创建失败: ${error.message || '未知错误'}`)
      }
    },
    showEditDialog(task) {
      // 将后端下划线命名字段映射到前端表单字段
      this.editTask = {
        taskId: task.task_id,
        taskName: task.task_name,
        taskDesc: task.task_description,
        assigneeId: task.receiver_id,
        priority: String(task.priority || 2),
        dueTime: task.finish_time,
        status: String(task.status || 1)
      }
      this.editDialogVisible = true
    },
    async updateTask() {
      try {
        const data = {
          task_name: this.editTask.taskName,
          description: this.editTask.taskDesc,
          receiver_id: this.editTask.assigneeId,
          priority: parseInt(this.editTask.priority) || 2,
          finish_time: this.editTask.dueTime ? new Date(this.editTask.dueTime).toISOString() : null,
          status: parseInt(this.editTask.status) || 1
        }
        await updateTask(this.editTask.taskId, data)
        this.editDialogVisible = false
        this.$message.success('任务更新成功')
        this.loadTasks()
      } catch (error) {
        console.error('更新任务失败:', error)
        this.$message.error(`更新失败: ${error.message || '未知错误'}`)
      }
    },
    async deleteTask(id) {
      try {
        await this.$confirm('确定要删除该任务吗？', '提示', { type: 'warning' })
        await deleteTask(id)
        this.$message.success('删除成功')
        this.loadTasks()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除任务失败:', error)
          this.$message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      }
    },
    // 获取状态文本
    getStatusText(status) {
      const map = { 1: '待执行', 2: '等依赖', 3: '执行中', 4: '已完成', 5: '已失败' }
      return map[status] || '未知'
    },
    // 获取状态Tag类型
    getStatusTagType(status) {
      const map = { 1: 'info', 2: 'warning', 3: 'primary', 4: 'success', 5: 'danger' }
      return map[status] || 'info'
    },
    // 获取优先级文本
    getPriorityText(priority) {
      const map = { 1: '高', 2: '中', 3: '低' }
      return map[priority] || '中'
    },
    // 获取优先级Tag类型
    getPriorityTagType(priority) {
      const map = { 1: 'danger', 2: 'warning', 3: 'info' }
      return map[priority] || 'info'
    },
    // 格式化时间
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    // 跳转到详情
    goToDetail(taskId) {
      this.$router.push({ name: 'TaskDetail', params: { id: taskId } })
    },
    // 分页大小变化
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.loadTasks()
    },
    // 页码变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadTasks()
    }
  }
}
</script>

<style lang="scss" scoped>
.task-manager {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background-color: var(--bg-page);
}

.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-light);
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-light);
}

.empty-text {
  text-align: center;
  color: var(--text-muted);
  padding: 60px 20px;
  font-size: 14px;
}

/* 看板视图 */
.kanban-column {
  background-color: var(--bg-page);
  border-radius: var(--radius-lg);
  padding: 16px;
  min-height: 500px;

  .column-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border-light);

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary);
    }

    .count {
      font-size: 14px;
      color: var(--text-muted);
      background-color: var(--bg-card);
      padding: 2px 10px;
      border-radius: var(--radius-full);
    }
  }
}

.kanban-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-card {
  background-color: var(--bg-card);
  border-radius: var(--radius-md);
  padding: 16px;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .card-content {
    h4 {
      margin: 0 0 8px 0;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .task-desc {
      margin: 0 0 12px 0;
      font-size: 12px;
      color: var(--text-secondary);
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      line-height: 1.5;
      min-height: 36px;
    }

    .task-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12px;
      color: var(--text-muted);
      margin-bottom: 8px;

      .assignee {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .due-time {
        color: var(--warning-color);
      }
    }

    .task-actions {
      display: flex;
      justify-content: flex-end;
    }
  }
}

/* 危险按钮 */
.danger {
  color: var(--danger-color);

  &:hover {
    color: var(--danger-light);
  }
}
</style>